import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { isApiErrorEnvelope } from '@/api/form-errors'
import { clearToken, getToken, setToken } from '@/api/auth'
import { AuthContext, type AuthContextValue, type AdminAuthUser } from '@/contexts/auth-context'

const ME_QUERY_KEY = ['admin', 'me'] as const

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [token, setTokenState] = useState<string | null>(() => getToken())

  const me = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: async (): Promise<AdminAuthUser> => {
      const { data, error } = await apiClient.GET('/admin/auth/me')
      if (error) throw error
      // A backend `/admin/auth/me` 200-on `{ data: AdminAuthUser }`-t ad
      // (DEC-ADMIN-001). A type-cast az openapi-shape-en él.
      const payload = (data as { data?: AdminAuthUser } | undefined)?.data
      if (!payload) throw new Error('Empty /admin/auth/me response')
      return payload
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  })

  // 401 vagy más auth-hiba esetén automatikus session-tisztítás (DEC-FE-021
  // mintát követve). A setState a useEffect-ben szándékos: aszinkron
  // query-error reakciója, és csak egyszer fut a hibás token-re.
  useEffect(() => {
    if (!me.isError || !token) return
    const error = me.error as unknown
    const is401 =
      isApiErrorEnvelope(error) && (error.code === 'unauthenticated' || error.code === 'forbidden')
    if (is401) {
      clearToken()
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTokenState(null)
      queryClient.removeQueries({ queryKey: ME_QUERY_KEY })
    }
  }, [me.isError, me.error, token, queryClient])

  const setSession = useCallback<AuthContextValue['setSession']>(
    ({ token: newToken, authUser: newUser }) => {
      setToken(newToken)
      setTokenState(newToken)
      queryClient.setQueryData(ME_QUERY_KEY, newUser)
    },
    [queryClient],
  )

  const clearSession = useCallback<AuthContextValue['clearSession']>(() => {
    clearToken()
    setTokenState(null)
    queryClient.removeQueries({ queryKey: ME_QUERY_KEY })
  }, [queryClient])

  const authUser = me.data ?? null
  const isInitializing = !!token && me.isPending && me.fetchStatus !== 'idle'

  return (
    <AuthContext.Provider value={{ authUser, token, isInitializing, setSession, clearSession }}>
      {children}
    </AuthContext.Provider>
  )
}
